// Dependencies
import { FC, useCallback, useContext, useEffect, useState, useMemo, useRef, type ComponentType } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Stack,
  IconButton,
  Backdrop,
  CircularProgress,
  Dialog as MuiDialog,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import { Check, Close, Save, Preview, Upload, Cancel, KeyboardReturn, Download, Settings, Autorenew, ContentCopy } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getTemplateComponents, defaultTemplate, TEMPLATE_ID_68ef31e18875863b2fa59822, TEMPLATE_ID_691f80ce0664e043e4a1986d, TEMPLATE_ID_69d0c43f5a2b1c0d9e8f7a6b, TEMPLATE_ID_69842241581723c403c697fc, TEMPLATE_ID_6995ea9e4c4ced0db3932d1a, TEMPLATE_ID_6995ec804c4ced0db3932d1b, TEMPLATE_ID_6995ed834c4ced0db3932d1c, TEMPLATE_ID_6a56437d260e22f9e9296ede, TEMPLATE_ID_6a98f0c8b6c2a1d4e5f60001 } from './components/templates';
import { FitPreviewWidth } from './FitPreviewWidth';
import styles from './styles';
import { getDocumentFields, getDocument, customizeDocument, previewDocument, uploadGeneratedPdf, forceGenerateFactsheet, downloadPdf, getPrFactsheetPreview } from '../../apis/pdf.apis';
import { pdf as buildPdf, Font } from '@react-pdf/renderer';
import http from '../../apis/http';
import { AppContext } from '../../context';
import { clearSessionTokens, isSessionGoneError, loadErrorMessage } from './loadErrors';
import { AutomationConfigDialog, ForceGenerateConfirmDialog } from '../../components/Dialogs';
import moment from 'moment';
import { getNextPublishingDate } from '../../utils/automationSchedule';
import { client } from '../../App';
import {
  buildFactsheetDownloadFileName,
  displayFileName,
  resolveFactsheetDownloadAsOf,
  toDocumentFileName,
  triggerBrowserDownload,
} from '../../utils/documentFileName';
import { useToastContext } from '../../components/Toast/toastContext';
import { CONFLICT_RELOAD_MESSAGE, isConflictError } from '../../utils/editorLock';
import { GET_QUERY } from '../../queries/history';
import { resolveManImageUrl } from './components/templates/69842241581723c403c697fc/templateConstants';
import { isPvmGpsTemplate } from './components/templates/69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import { isPrRealValueTemplate } from './components/templates/pr-real-value/templateConstants';
import { mergePrPreviewWithV2Data } from './components/templates/pr-real-value/mappers';
import { PVM_GPS_RIGHT_LOGO, resolvePvmGpsLeftLogoUrl } from './components/templates/69d0c43f5a2b1c0d9e8f7a6b/pvmHeader';
import { resolveFactsheetUntilIso } from './components/templates/69842241581723c403c697fc/factsheetDates';
import { blobToDataUrl, S3_PREFIX, fetchImageForPdf } from './components/templates/69842241581723c403c697fc/s3PdfImage';
import {
  normalizeSquadAdvisors,
  SQUAD_ADVISOR_FOOTER_LOGO,
} from './components/templates/6a56437d260e22f9e9296ede/squadAdvisors';
import { ensureSquadTitleMeasureFont } from './components/templates/6a56437d260e22f9e9296ede/squadTitleFit';
import {
  fetchSquadDynamicBundle,
  type SquadDynamicBundle,
} from './components/templates/6a56437d260e22f9e9296ede/fetchSquadDynamicBundle';

// // Register additional fonts at module level so they're always available
// // Todo: need to integrate as additional_fonts field in template
// Font.register({
//   family: 'Cambria',
//   fonts: [{
//     src: 'https://cixon-finance-docs.s3.amazonaws.com/uploads/2026-02-06T17-11-47-008Z-cambria.ttf',
//     fontWeight: 700,
//     fontStyle: 'normal',
//   }],
// });

// Export PDF Viewer V2 page
export const PDFViewerV2: FC = () => {
  const [data, setData] = useState<any>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileNameActive, setFileNameActive] = useState<boolean>(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);
  const [originalFields, setOriginalFields] = useState<any | null>(null);
  const lockUpdatedAtRef = useRef<string | undefined>(undefined);
  const [conflictOpen, setConflictOpen] = useState(false);
  /** Set when the document or its fields failed to load — the editor then shows nothing real. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lineChartLoading, setLineChartLoading] = useState<boolean>(false);
  const [pieChartImages, setPieChartImages] = useState<{ countryChartUrl?: string; sectorChartUrl?: string }>({});
  const [isResetPreview, setResetPreview] = useState(false);
  const [templateFontFamily, setTemplateFontFamily] = useState<string | null>(null);
  const [automationConfigOpen, setAutomationConfigOpen] = useState(false);
  const [forceGenerateOpen, setForceGenerateOpen] = useState(false);
  const [isAutomated, setIsAutomated] = useState<boolean>(false);
  const [automationFrequency, setAutomationFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');
  const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(null);

  /** Latest factsheet data (synced on every onChange) — use after PdfConfig flush so Save sees pending debounced edits */
  const dataRef = useRef<any>(null);
  const pdfConfigFlushRef = useRef<(() => void) | null>(null);
  const chartCaptureRef = useRef<(() => Promise<void>) | null>(null);
  const pieChartImagesRef = useRef<{ countryChartUrl?: string; sectorChartUrl?: string }>({});
  const leftScrollRef = useRef<HTMLDivElement | null>(null);
  const rightScrollRef = useRef<HTMLDivElement | null>(null);
  const leftScrollTopRef = useRef(0);
  const rightScrollTopRef = useRef(0);
  const handleRegisterPdfConfigFlush = useCallback((fn: () => void) => {
    pdfConfigFlushRef.current = fn;
  }, []);
  const handleRegisterChartCapture = useCallback((capture: () => Promise<void>) => {
    chartCaptureRef.current = capture;
  }, []);

  const { document } = useContext(AppContext);

  const { id } = useParams<{ id: string }>();
  const { account, setDocument, setAccount } = useContext(AppContext);
  const { activeToast } = useToastContext();

  const applyOriginalFields = (fields: any) => {
    setOriginalFields(fields || {});
    if (fields?.updatedAt) lockUpdatedAtRef.current = fields.updatedAt;
  };

  const notifyEditorError = (err: any, fallback = 'Save failed') => {
    if (isConflictError(err)) {
      setConflictOpen(true);
      return;
    }
    activeToast({ type: 'error', text: err?.msg || fallback });
  };

  // Get template components based on template ID
  const templateId = document?.template?._id;
  const templateComponents = useMemo(() => {
    return getTemplateComponents(templateId) || defaultTemplate;
  }, [templateId]);

  const { PdfConfig, FactsheetPreview, PdfPreviewDialog, FactsheetPDF, mapV1FieldsToV2Data, buildCustomizePayload } = templateComponents;

  const nextScheduledLabel = useMemo(() => {
    if (!isAutomated) return '';
    const baseDate = lastPublishedAt ? moment(lastPublishedAt) : moment();
    if (!baseDate.isValid()) return '';
    return getNextPublishingDate(baseDate, automationFrequency).format('DD.MM.YYYY');
  }, [isAutomated, lastPublishedAt, automationFrequency]);

  const todayLabel = useMemo(() => moment().format('DD.MM.YYYY'), []);

  /** Save / Publish / Force Generate only act on a document whose fields actually loaded. */
  const documentReady = Boolean(originalFields) && !loadError;
  const isBackupDoc = Boolean(document?.isBackup);
  const canEditDocument = documentReady && !isBackupDoc;

  // Register template-specific fonts from backend template data
  useEffect(() => {
    const tplFonts: any = (document as any)?.template?.fonts;
    if (!tplFonts || !tplFonts.family || !Array.isArray(tplFonts.fonts) || tplFonts.fonts.length === 0) {
      return;
    }

    // Register fonts for react-pdf (PDF generation)
    try {
      Font.register({
        family: tplFonts.family,
        fonts: tplFonts.fonts.map((f: any) => ({
          src: f.src,
          fontWeight: f.fontWeight,
          fontStyle: f.fontStyle,
        })),
      });

    } catch {
      // ignore font registration errors to avoid breaking the viewer
    }

    // Register fonts for browser preview using CSS @font-face
    try {
      setTemplateFontFamily(tplFonts.family);
      const styleId = `template-fonts-${tplFonts.family}`;
      // Remove existing style if present
      const existingStyle = window.document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      // Create style element with @font-face rules
      const style = window.document.createElement('style');
      style.id = styleId;
      const fontFaceRules = tplFonts.fonts.map((f: any) => {
        const fontWeight = f.fontWeight || 'normal';
        const fontStyle = f.fontStyle || 'normal';
        return `
          @font-face {
            font-family: '${tplFonts.family}';
            src: url('${f.src}');
            font-weight: ${fontWeight};
            font-style: ${fontStyle};
            font-display: swap;
          }

        `;
      }).join('\n');

      style.textContent = fontFaceRules;
      window.document.head.appendChild(style);

      // Set CSS variable for template font family so preview components can use it
      const root = window.document.documentElement;
      root.style.setProperty('--template-font-family', `'${tplFonts.family}'`);

      // Cleanup function
      return () => {
        const styleToRemove = window.document.getElementById(styleId);
        if (styleToRemove) {
          styleToRemove.remove();
        }
        root.style.removeProperty('--template-font-family');
        setTemplateFontFamily(null);
      };
    } catch (error) {
      console.error('Error loading fonts for browser preview:', error);
    }
  }, [templateId, document]);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const fieldsRes: any = await getDocumentFields(id);
        if (!isMounted) return;
        applyOriginalFields(fieldsRes?.fields || {});

        const docRes = await getDocument(account?.id, id);
        if (!isMounted) return;
        setDocument(docRes);

        const v2Data = mapV1FieldsToV2Data(fieldsRes?.fields || {}, docRes?.template?._id);
        setData(v2Data);
        dataRef.current = v2Data;
        setFileName(displayFileName(docRes?.name));
        
        // Set automation status - check both isAutomated and automationMode for compatibility
        const isAutomatedDoc = docRes?.isAutomated || docRes?.automationMode === 'automation';
        setIsAutomated(isAutomatedDoc);
        setAutomationFrequency(docRes?.automationFrequency || 'daily');
        // For automated documents, published_at is updated by backend when PDF is generated
        setLastPublishedAt(docRes?.published_at || null);
        
        // Fetch live preview data (P&R resolver on server, history chart for others).
        // The document itself is loaded at this point, so a failure here stays non-fatal.
        try {
          await refreshFactsheetPreviewData(docRes, fieldsRes?.fields || {}, v2Data);
        } catch (e) {
          console.warn('[PDFViewerV2] Vorschaudaten konnten nicht geladen werden', e);
        }
      } catch (e: any) {
        if (!isMounted || e?.canceled) return;
        // Swallowing this left an empty editor that looked like a factsheet without data —
        // and whose Publish would have uploaded that empty PDF. Say why, and lock the actions.
        console.error('[PDFViewerV2] Factsheet konnte nicht geladen werden', { id, error: e });
        if (isSessionGoneError(e)) {
          // No session left (logged out elsewhere, user removed): back to login, not a blank editor
          clearSessionTokens();
          setAccount(null);
          return;
        }
        setLoadError(loadErrorMessage(e));
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => { isMounted = false; };
  }, [id, account?.id, setDocument]);

  const onChange = useCallback((data: any) => {
    if (leftScrollRef.current) leftScrollTopRef.current = leftScrollRef.current.scrollTop;
    if (rightScrollRef.current) rightScrollTopRef.current = rightScrollRef.current.scrollTop;
    dataRef.current = data;
    setData(data);
  }, []);

  // Restore column scroll after preview/config re-renders from data updates.
  useEffect(() => {
    const left = leftScrollRef.current;
    const right = rightScrollRef.current;
    if (left && Math.abs(left.scrollTop - leftScrollTopRef.current) > 1) {
      left.scrollTop = leftScrollTopRef.current;
    }
    if (right && Math.abs(right.scrollTop - rightScrollTopRef.current) > 1) {
      right.scrollTop = rightScrollTopRef.current;
    }
  }, [data]);

  const handleChartImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.chartImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), chartImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handleChart1ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.chart1ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), chart1ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handleChart2ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.chart2ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), chart2ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handleChart3ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.chart3ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), chart3ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handlePieChart1ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.pieChart1ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), pieChart1ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handlePieChart2ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.pieChart2ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), pieChart2ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);

  const handlePieChart3ImageChange = useCallback((dataUrl?: string) => {
    setData((prev: any) => {
      if ((prev?.pieChart3ImageDataUrl || undefined) === (dataUrl || undefined)) return prev;
      const next = { ...(prev || {}), pieChart3ImageDataUrl: dataUrl };
      dataRef.current = next;
      return next;
    });
  }, []);


  const handlePieChartImagesChange = useCallback((countryChartUrl?: string, sectorChartUrl?: string) => {
    const next = { countryChartUrl, sectorChartUrl };
    pieChartImagesRef.current = next;
    setPieChartImages((prev) => {
      if (prev.countryChartUrl === countryChartUrl && prev.sectorChartUrl === sectorChartUrl) return prev;
      return next;
    });
  }, []);

  const waitForChartCaptureBeforePdf = useCallback(async () => {
    if (chartCaptureRef.current) {
      await chartCaptureRef.current();
    }
    await new Promise((resolve) => setTimeout(resolve, 150));
  }, []);

  // Helper function to format date to YYYY-MM-DD
  const formatDateToUntil = useCallback((dateStr: string | undefined): string | undefined => {
    if (!dateStr) return undefined;
    const raw = String(dateStr).trim();
    // Preserve explicit calendar dates from ISO/timestamp values (avoid timezone day shifts).
    const datePart = raw.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (datePart) {
      return moment(datePart, 'YYYY-MM-DD', true).isValid() ? datePart : undefined;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return undefined;
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Fetch history chart data using same logic as V1, with optional overrides from V2 data
  const fetchAndSetHistoryChart = useCallback(async (docRes: any, fieldsRes: any, v2DataLocal?: any) => {
    try {
      setLineChartLoading(true);
      const type = docRes?.template?.isins?.length ? 'Axxion' : 'SolActive';
      /**
       * mode 'all' decimates the series to ~100 points server-side (hard cap in
       * getAxxionHistoryChart), which turns a 20-year NAV curve into quarterly
       * samples. SQUAD needs the undecimated daily series; every other template
       * keeps the existing behaviour.
       */
      const historyMode = docRes?.template?._id === TEMPLATE_ID_6a56437d260e22f9e9296ede ? '' : 'all';
      const isin = (v2DataLocal?.historyIsin || data?.historyIsin || docRes?.template?.isins?.[0]) as string | undefined;
      const until =
        resolveFactsheetUntilIso(v2DataLocal || data) ||
        formatDateToUntil(v2DataLocal?.published_at || data?.published_at) ||
        '';
      if (!isin) {
        setData((prev: any) => {
          const next = { ...(prev || {}), lineChartData: [] };
          dataRef.current = next;
          return next;
        });
        return;
      }
      const res: any = await client.query({
        query: GET_QUERY,
        variables: { param: { type, isin, mode: historyMode, until: until || '' } },
        fetchPolicy: 'network-only',
      });
      const histories = res?.data?.getQuery?.histories || [];
      if (process.env.NODE_ENV !== 'production') {
        const lastTs = histories?.length ? histories[histories.length - 1]?.[0] : undefined;
        const lastDate = lastTs ? moment(lastTs).format('YYYY-MM-DD') : 'none';
        console.info('[PDFViewerV2][history-chart]', {
          type,
          isin,
          rawPublishedAt: v2DataLocal?.published_at || data?.published_at,
          untilUsed: until || '',
          points: histories.length,
          lastDate,
        });
      }
      setData((prev: any) => {
        const next = { ...(prev || {}), lineChartData: histories };
        dataRef.current = next;
        return next;
      });
    } finally {
      setLineChartLoading(false);
    }
  }, [data?.historyIsin, data?.published_at, formatDateToUntil]);

  const fetchPrFactsheetPreview = useCallback(
    async (docRes: any, fieldsRes: any, v2DataLocal?: any) => {
      if (!id || !isPrRealValueTemplate(docRes?.template?._id)) return;
      try {
        setLineChartLoading(true);
        const until = resolveFactsheetUntilIso(v2DataLocal || dataRef.current) || '';
        const res: any = await getPrFactsheetPreview(id, until ? { until } : undefined);
        const preview = res?.data || res;
        const v2Base = mapV1FieldsToV2Data(fieldsRes || {}, docRes?.template?._id);
        const local = { ...(v2DataLocal || {}), ...(dataRef.current || {}) };
        const merged = mergePrPreviewWithV2Data(preview, {
          ...v2Base,
          title: local.title ?? v2Base.title,
          investmentStyle: local.investmentStyle ?? v2Base.investmentStyle,
          shareClassHistoryNote: local.shareClassHistoryNote ?? v2Base.shareClassHistoryNote,
          disclaimer: local.disclaimer ?? v2Base.disclaimer,
        });
        setData((prev: any) => {
          const next = { ...(prev || {}), ...merged };
          dataRef.current = next;
          return next;
        });
      } catch (error) {
        console.error('[P&R preview] failed to load server factsheet data', error);
      } finally {
        setLineChartLoading(false);
      }
    },
    [id, mapV1FieldsToV2Data]
  );

  const refreshFactsheetPreviewData = useCallback(
    async (docRes: any, fieldsRes: any, v2DataLocal?: any) => {
      if (isPrRealValueTemplate(docRes?.template?._id)) {
        await fetchPrFactsheetPreview(docRes, fieldsRes, v2DataLocal);
        return;
      }
      await fetchAndSetHistoryChart(docRes, fieldsRes, v2DataLocal);
    },
    [fetchAndSetHistoryChart, fetchPrFactsheetPreview]
  );

  // Refetch when document loads or valuation date / ISIN changes
  useEffect(() => {
    if (!document || !originalFields) return;
    refreshFactsheetPreviewData(document, originalFields, data || {});
  }, [
    document,
    originalFields,
    data?.historyIsin,
    data?.published_at,
    refreshFactsheetPreviewData,
  ]);

  const handleChangeFileName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const handleUpdateFileName = async () => {
    // With nothing loaded the name box is empty — renaming would blank the real name
    if (!id || !account?.id || !canEditDocument) return;
    setFileNameActive(false);
    const name = toDocumentFileName(fileName);
    try {
      await customizeDocument(
        account.id,
        id,
        [{ key: 'name', value: name }],
        originalFields?.updatedAt
      );
      const [docRes, fieldsRes] = await Promise.all([
        getDocument(account.id, id),
        getDocumentFields(id),
      ]);
      setDocument(docRes);
      applyOriginalFields(fieldsRes?.fields || {});
      setFileName(displayFileName(docRes?.name));
    } catch {
      setFileName(displayFileName(document?.name));
    }
  };

  const handleCancelUpdateFileName = () => {
    setFileNameActive(false);
    setFileName(displayFileName(document?.name));
  };

  const handleFileNameActive = () => {
    setFileNameActive(true);
  };

  const handleForceGenerateClick = () => {
    setForceGenerateOpen(true);
  };

  const handleForceGenerateClose = () => {
    if (!testing) {
      setForceGenerateOpen(false);
    }
  };

  const buildAndUploadPdfToS3 = useCallback(async () => {
    if (!id || !account?.id) return;
    try {
      await waitForChartCaptureBeforePdf();
    } catch (e) {
      console.error('Chart capture before PDF upload failed:', e);
    }

    let logoSrc: string | undefined = undefined;
    let rightLogoSrc: string | undefined = undefined;
    const templateId = document?.template?._id as string | undefined;

    if (isPvmGpsTemplate(templateId)) {
      const iconUrl = resolvePvmGpsLeftLogoUrl(document?.template?.icon_url as string | undefined);
      if (iconUrl) {
        try {
          logoSrc = (await fetchImageForPdf(iconUrl)) || iconUrl;
        } catch (e) {
          console.error('Failed to load PVM left logo for PDF upload:', e);
          logoSrc = iconUrl;
        }
      }
      try {
        rightLogoSrc = (await fetchImageForPdf(PVM_GPS_RIGHT_LOGO)) || PVM_GPS_RIGHT_LOGO;
      } catch (e) {
        console.error('Failed to load PVM right logo for PDF upload:', e);
        rightLogoSrc = PVM_GPS_RIGHT_LOGO;
      }
    } else {
      const iconUrl = document?.template?.icon_url as string | undefined;
      if (iconUrl) {
        try {
          const s3Key = iconUrl.replace(S3_PREFIX, '').split('?')[0].split('#')[0];
          const downloadUrl = `/docs/files/download/${s3Key}`;
          const logoBlob: Blob = await http.getBlob(downloadUrl);
          logoSrc = await blobToDataUrl(logoBlob, iconUrl);
        } catch (e) {
          console.log(e);
        }
      }
    }

    const current = dataRef.current || {};
    const mergedTemplate = { ...(current?.template || {}), ...(document?.template || {}) };
    let manImageSrc: string | undefined;
    const manImageUrl = resolveManImageUrl(
      mergedTemplate?._id,
      current?.manImageUrl,
      mergedTemplate?.manImageUrl,
    );
    if (manImageUrl) {
      const isPlaceholder =
        manImageUrl === '/images/intro.png' || manImageUrl.endsWith('/images/intro.png');
      if (!(isPvmGpsTemplate(templateId) && isPlaceholder)) {
        try {
          manImageSrc = await fetchImageForPdf(manImageUrl);
        } catch (e) {
          console.error('Failed to load man image for PDF upload:', e);
        }
      }
    }

    let historischeAssetAllokationImageSrc: string | undefined;
    let investmentprozessImageSrc: string | undefined;
    let zusammenfassungImageSrc: string | undefined;
    let ratingsImageSrc: string | undefined;
    let advisorsForPdf: ReturnType<typeof normalizeSquadAdvisors> | undefined;
    let advisorFooterLogoSrc: string | undefined;
    let fondspartnerLogoSrc: string | undefined;
    let squadFondsdatenCache: Map<string, string> | undefined;
    let squadSectionHeaderCache: Map<string, string> | undefined;
    let squadSrri: number | null = null;
    let squadPerformanceRiskTable: SquadDynamicBundle['performanceRiskTable'] | undefined;
    let squadWeitereDaten: SquadDynamicBundle['weitereDaten'] | undefined;
    if (templateId === TEMPLATE_ID_6a56437d260e22f9e9296ede) {
      // Title autofit measures with canvas; without the embedded TTF it underestimates width
      // and long titles wrap under the first section bar on published PDFs.
      try {
        await ensureSquadTitleMeasureFont();
      } catch (e) {
        console.error('Failed to load SQUAD title measure font for PDF upload:', e);
      }
      const resolveSquadSlot = async (url?: string) => {
        const raw = typeof url === 'string' ? url.trim() : '';
        if (!raw) return undefined;
        if (raw.startsWith('data:')) return raw;
        return (await fetchImageForPdf(raw)) || undefined;
      };
      try {
        const advisorsIn = normalizeSquadAdvisors(current?.advisors);
        let squadDynamic: SquadDynamicBundle | undefined;
        [
          historischeAssetAllokationImageSrc,
          investmentprozessImageSrc,
          zusammenfassungImageSrc,
          ratingsImageSrc,
          advisorFooterLogoSrc,
          fondspartnerLogoSrc,
          advisorsForPdf,
          squadDynamic,
        ] = await Promise.all([
          resolveSquadSlot(current?.historischeAssetAllokationImageUrl),
          resolveSquadSlot(current?.investmentprozessImageUrl),
          resolveSquadSlot(current?.zusammenfassungImageUrl),
          resolveSquadSlot(current?.ratingsImageUrl),
          resolveSquadSlot(current?.advisorLogoImageUrl),
          resolveSquadSlot(current?.fondspartnerLogoImageUrl),
          Promise.all(
            advisorsIn.map(async (a) => ({
              ...a,
              photoUrl: await resolveSquadSlot(a.photoUrl),
              logoUrl: await resolveSquadSlot(a.logoUrl),
            })),
          ),
          // Same values as the PDF dialog, SRRI included — see fetchSquadDynamicBundle.
          fetchSquadDynamicBundle(current, mergedTemplate),
        ]);
        if (squadDynamic) {
          squadFondsdatenCache = squadDynamic.fondsdatenCache;
          squadSectionHeaderCache = squadDynamic.sectionHeaderCache;
          squadSrri = squadDynamic.srri;
          squadPerformanceRiskTable = squadDynamic.performanceRiskTable;
          squadWeitereDaten = squadDynamic.weitereDaten;
          if (squadDynamic.missing.length) {
            console.warn(
              `SQUAD publish: no data for ${squadDynamic.missing.join(', ')} (${squadDynamic.isin}, ${squadDynamic.until || 'latest'})`,
            );
          }
        }
      } catch (e) {
        console.error('Failed to load SQUAD slot images for PDF upload:', e);
      }
    }

    const PdfDocumentComponent = FactsheetPDF as ComponentType<{
      data: any;
      logoSrc?: string;
      rightLogoSrc?: string;
      manImageSrc?: string;
      page2ImageSrc?: string;
      page2ImageSrc2?: string;
      page2ImageSrc3?: string;
      pieChartImages?: { countryChartUrl?: string; sectorChartUrl?: string };
      historischeAssetAllokationImageSrc?: string;
      investmentprozessImageSrc?: string;
      zusammenfassungImageSrc?: string;
      ratingsImageSrc?: string;
      advisorsForPdf?: ReturnType<typeof normalizeSquadAdvisors>;
      advisorFooterLogoSrc?: string;
      fondspartnerLogoSrc?: string;
      fondsdatenCache?: Map<string, string>;
      sectionHeaderCache?: Map<string, string>;
    }>;

    let page2ImageSrc: string | undefined;
    let page2ImageSrc2: string | undefined;
    let page2ImageSrc3: string | undefined;
    if (isPvmGpsTemplate(templateId)) {
      const resolvePage2 = async (url?: string) => {
        const raw = typeof url === 'string' ? url.trim() : '';
        if (!raw) return undefined;
        if (raw.startsWith('data:')) return raw;
        return (await fetchImageForPdf(raw)) || undefined;
      };
      try {
        [page2ImageSrc, page2ImageSrc2, page2ImageSrc3] = await Promise.all([
          resolvePage2(current?.page2ImageUrl),
          resolvePage2(current?.page2ImageUrl2),
          resolvePage2(current?.page2ImageUrl3),
        ]);
      } catch (e) {
        console.error('Failed to load PVM page-2 images for PDF upload:', e);
      }
    }

    const blob = await buildPdf(
      <PdfDocumentComponent
        data={{
          ...current,
          template: mergedTemplate,
          // SQUAD: the SRRI as of the factsheet date, not the level stored when the form opened.
          ...(squadSrri != null ? { riskIndicator: squadSrri } : {}),
          ...(squadPerformanceRiskTable ? { performanceRiskTable: squadPerformanceRiskTable } : {}),
          ...(squadWeitereDaten ? { weitereDaten: squadWeitereDaten } : {}),
        }}
        logoSrc={logoSrc}
        rightLogoSrc={rightLogoSrc}
        manImageSrc={manImageSrc}
        page2ImageSrc={page2ImageSrc}
        page2ImageSrc2={page2ImageSrc2}
        page2ImageSrc3={page2ImageSrc3}
        pieChartImages={pieChartImagesRef.current}
        historischeAssetAllokationImageSrc={historischeAssetAllokationImageSrc}
        investmentprozessImageSrc={investmentprozessImageSrc}
        zusammenfassungImageSrc={zusammenfassungImageSrc}
        ratingsImageSrc={ratingsImageSrc}
        advisorsForPdf={advisorsForPdf}
        advisorFooterLogoSrc={advisorFooterLogoSrc}
        fondspartnerLogoSrc={fondspartnerLogoSrc}
        fondsdatenCache={squadFondsdatenCache}
        sectionHeaderCache={squadSectionHeaderCache}
      />,
    ).toBlob();
    const meta = {
      file_name: toDocumentFileName(fileName || document?.name || 'factsheet'),
      published_at: dataRef.current?.published_at,
      template_id: document?.template?._id,
      document_id: id,
      expectedUpdatedAt: lockUpdatedAtRef.current || originalFields?.updatedAt,
    };
    await uploadGeneratedPdf(account.id, id, blob, meta);
    // Refresh lock token after fields may have been touched by upload (published_at).
    try {
      const fieldsRes: any = await getDocumentFields(id);
      if (fieldsRes?.fields) applyOriginalFields(fieldsRes.fields);
    } catch {
      /* keep previous lock token */
    }
    const docRes = await getDocument(account.id, id);
    setDocument(docRes);
    setFileName(displayFileName(docRes?.name));
  }, [
    account?.id,
    document?.name,
    document?.template,
    fileName,
    id,
    waitForChartCaptureBeforePdf,
  ]);

  const handleForceGenerateConfirm = async () => {
    if (!id || !originalFields) return;
    try {
      setTesting(true);
      flushSync(() => {
        pdfConfigFlushRef.current?.();
      });
      const payload = buildCustomizePayload(dataRef.current, originalFields);
      if (payload.length) {
        await customizeDocument(account?.id, id, payload, originalFields?.updatedAt);
        const fieldsRes: any = await getDocumentFields(id);
        applyOriginalFields(fieldsRes?.fields || {});
      }
      const result = await forceGenerateFactsheet(account?.id, id);
      if (result?.success) {
        const [docRes, fieldsRes] = await Promise.all([
          getDocument(account?.id, id),
          getDocumentFields(id),
        ]);
        setDocument(docRes);
        setLastPublishedAt(docRes?.published_at || null);
        const v2Data = mapV1FieldsToV2Data(fieldsRes?.fields || {}, docRes?.template?._id);
        setData(v2Data);
        dataRef.current = v2Data;
        applyOriginalFields(fieldsRes?.fields || {});
        await refreshFactsheetPreviewData(docRes, fieldsRes?.fields || {}, v2Data);
        setForceGenerateOpen(false);
      }
    } catch (e) {
      console.error('Force generate error:', e);
      notifyEditorError(e, 'Force generate failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!id || !originalFields || isBackupDoc) return;
    flushSync(() => {
      pdfConfigFlushRef.current?.();
    });
    const payload = buildCustomizePayload(dataRef.current, originalFields);
    if (!payload.length) return;
    try {
      setSaving(true);
      await customizeDocument(account?.id, id, payload, originalFields?.updatedAt);
      const fieldsRes: any = await getDocumentFields(id);
      applyOriginalFields(fieldsRes?.fields || {});
      if (fieldsRes?.fields?.updatedAt) {
        lockUpdatedAtRef.current = fieldsRes.fields.updatedAt;
      }
      const v2Data = mapV1FieldsToV2Data(fieldsRes?.fields || {}, document?.template?._id);
      // Preserve runtime data that's not stored in the database (chart data, images, etc.)
      setData((prev: any) => ({
        ...v2Data,
        lineChartData: prev?.lineChartData, // Preserve chart data
        chartImageDataUrl: prev?.chartImageDataUrl,
        chart1ImageDataUrl: prev?.chart1ImageDataUrl,
        chart2ImageDataUrl: prev?.chart2ImageDataUrl,
        chart3ImageDataUrl: prev?.chart3ImageDataUrl,
        pieChart1ImageDataUrl: prev?.pieChart1ImageDataUrl,
        pieChart2ImageDataUrl: prev?.pieChart2ImageDataUrl,
        pieChart3ImageDataUrl: prev?.pieChart3ImageDataUrl,
      }));
      if (document) {
        await refreshFactsheetPreviewData(document, fieldsRes?.fields || {}, v2Data);
      }
      if (!isAutomated && !isPrRealValueTemplate(document?.template?._id)) {
        await buildAndUploadPdfToS3();
      }
    } catch (e) {
      console.error('Save failed:', e);
      notifyEditorError(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = () => {
    // Reserved for future parity with V1 synchronize flow
  };

  const handlePreview = async () => {
    flushSync(() => {
      pdfConfigFlushRef.current?.();
    });
    try {
      await waitForChartCaptureBeforePdf();
      flushSync(() => {
        if (dataRef.current) setData({ ...dataRef.current });
      });
    } catch (e) {
      console.error('Chart capture before preview failed:', e);
    }
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
  };

  const handlePublish = async () => {
    // Never publish what did not load — it would upload an empty PDF over the live one
    if (!id || !canEditDocument) return;
    if (isPrRealValueTemplate(document?.template?._id)) return;
    try {
      flushSync(() => {
        pdfConfigFlushRef.current?.();
      });
      setSaving(true);
      await waitForChartCaptureBeforePdf();
      flushSync(() => {
        if (dataRef.current) setData({ ...dataRef.current });
      });
      await buildAndUploadPdfToS3();
    } catch (e) {
      console.log(e);
      notifyEditorError(e, 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleResetPreviewStarted = () => {
    setResetPreview(false);
    setPreviewDialogOpen(true);
  };

  // Handle download for automated documents — predefined Factsheet_{Title}_{YYYYMMDD}.pdf
  const handleDownload = async () => {
    if (!document?.file_url) return;
    try {
      const blob = await downloadPdf(document.file_url);
      const downloadName = buildFactsheetDownloadFileName({
        customTemplate: data?.pdf_download_name,
        title:
          data?.title ||
          document?.template?.fond ||
          document?.name ||
          fileName ||
          'Factsheet',
        asOf: resolveFactsheetDownloadAsOf(data),
      });
      triggerBrowserDownload(blob, downloadName);
    } catch (e) {
      console.error('Download error:', e);
    }
  };

  /** Stable S3 URL for client websites (drop cache-bust ?v= so the same embed always points at the object key). */
  const handleCopyPdfLink = async () => {
    const raw = document?.file_url;
    if (!raw) return;
    const url = String(raw).split('?')[0].split('#')[0];
    try {
      await navigator.clipboard.writeText(url);
      activeToast({ type: 'success', text: 'PDF link copied' });
    } catch (e) {
      console.error('Copy PDF link error:', e);
      activeToast({ type: 'error', text: 'Failed to copy PDF link' });
    }
  };

  // Handle automation config
  const handleAutomationConfig = () => {
    setAutomationConfigOpen(true);
  };

  const handleAutomationConfigClose = () => {
    setAutomationConfigOpen(false);
  };

  const handleAutomationConfigSave = async (config: { isAutomated: boolean; automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly' }) => {
    if (!id || !account?.id || !canEditDocument) return;
    try {
      setSaving(true);
      await customizeDocument(
        account.id,
        id,
        [
          { key: 'isAutomated', value: config.isAutomated },
          { key: 'automationFrequency', value: config.automationFrequency || null },
        ],
        originalFields?.updatedAt
      );

      const [docRes, fieldsRes] = await Promise.all([
        getDocument(account.id, id),
        getDocumentFields(id),
      ]);
      setDocument(docRes);
      applyOriginalFields(fieldsRes?.fields || {});
      setIsAutomated(config.isAutomated);
      setAutomationFrequency(config.automationFrequency || 'daily');

      setAutomationConfigOpen(false);
    } catch (e) {
      console.error('Error updating automation config:', e);
      notifyEditorError(e, 'Could not update automation');
    } finally {
      setSaving(false);
    }
  };

  // FitPreviewWidth is only for fixed-width previews (SQUAD 794 / P&R 900).
  // Fluid templates (SHV, RV, …) use width:100% — wrapping them in max-content
  // collapses the preview into a thin squashed strip.
  const useFitPreviewWidth =
    templateId === TEMPLATE_ID_6a56437d260e22f9e9296ede || isPrRealValueTemplate(templateId);

  return (
    <Box sx={styles.wrapper}>
      <Backdrop open={loading || saving || (testing && !forceGenerateOpen)} sx={{ zIndex: 1300 }}>
        <CircularProgress />
      </Backdrop>
      <Box>
        <Box
          position="fixed"
          width="calc(100% - 780px)"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={10}
          top="28px"
          zIndex={10}
        >
          <Box sx={styles.fileName}>
            <input
              value={fileName}
              onChange={handleChangeFileName}
              onClick={handleFileNameActive}
              onFocus={handleFileNameActive}
              placeholder="File name"
              aria-label="File name"
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={4}
              sx={{ flexShrink: 0, display: fileNameActive ? 'flex' : 'none' }}
            >
              <IconButton sx={{ width: 32, height: 32 }} onClick={handleUpdateFileName}>
                <Check />
              </IconButton>
              <IconButton sx={{ width: 32, height: 32 }} size="small" onClick={handleCancelUpdateFileName}>
                <Close />
              </IconButton>
            </Stack>
          </Box>
        </Box>
        <Box sx={styles.actions}>
          {!isAutomated ? (
            <>
              <Button
                startIcon={<Save />}
                onClick={handleSaveChanges}
                disabled={!canEditDocument || saving}
                title={isBackupDoc ? 'Backups are read-only' : 'Save'}
                aria-label="Save"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Save</Box>
              </Button>
              <Button
                startIcon={<Preview />}
                onClick={handlePreview}
                title="Preview"
                aria-label="Preview"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Preview</Box>
              </Button>
              <Button
                startIcon={<Upload />}
                onClick={handlePublish}
                disabled={!canEditDocument}
                title={isBackupDoc ? 'Backups are read-only' : 'Publish'}
                aria-label="Publish"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Publish</Box>
              </Button>
              <Button
                startIcon={<KeyboardReturn />}
                onClick={handleBack}
                title="Back"
                aria-label="Back"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Back</Box>
              </Button>
            </>
          ) : (
            <>
              <Button
                startIcon={<Save />}
                onClick={handleSaveChanges}
                disabled={!canEditDocument || saving}
                title={isBackupDoc ? 'Backups are read-only' : 'Save'}
                aria-label="Save"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Save</Box>
              </Button>
              <Button
                startIcon={<Autorenew />}
                onClick={handleForceGenerateClick}
                disabled={testing || !canEditDocument}
                title={isBackupDoc ? 'Backups are read-only' : 'Force Generate'}
                aria-label="Force Generate"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Force Generate</Box>
              </Button>
              <Button
                startIcon={<Download />}
                onClick={handleDownload}
                disabled={!document?.file_url}
                title="Download"
                aria-label="Download"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Download</Box>
              </Button>
              <Button
                startIcon={<ContentCopy />}
                onClick={handleCopyPdfLink}
                disabled={!document?.file_url}
                title="Copy public PDF link for client websites"
                aria-label="Copy link"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Copy link</Box>
              </Button>
              <Button
                startIcon={<Settings />}
                onClick={handleAutomationConfig}
                title="Config"
                aria-label="Config"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Config</Box>
              </Button>
              <Button
                startIcon={<KeyboardReturn />}
                onClick={handleBack}
                title="Back"
                aria-label="Back"
                sx={styles.actionButton}
              >
                <Box component="span" sx={styles.actionLabel}>Back</Box>
              </Button>
            </>
          )}
        </Box>
      </Box>

      {loadError ? (
        <Alert
          severity="error"
          // Below the fixed file-name / actions bar, like the columns (leftWrapper pt 72)
          sx={{ mt: 72, mb: 12 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Neu laden
            </Button>
          }
        >
          Dieses Factsheet konnte nicht geladen werden ({loadError}). Speichern und Veröffentlichen
          sind gesperrt, damit nichts überschrieben wird.
        </Alert>
      ) : null}

      {isBackupDoc && !loadError ? (
        <Alert severity="info" sx={{ mt: 72, mb: 12 }}>
          This is a read-only backup. Restore it as master from the Backups list to edit.
        </Alert>
      ) : null}

      <MuiDialog open={conflictOpen} onClose={() => setConflictOpen(false)}>
        <DialogTitle>{CONFLICT_RELOAD_MESSAGE}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConflictOpen(false)}>Cancel</Button>
          <Button onClick={() => window.location.reload()} variant="contained">
            Reload
          </Button>
        </DialogActions>
      </MuiDialog>

      <Box sx={styles.columns}>
        <Box
          ref={leftScrollRef}
          sx={styles.leftWrapper}
          onScroll={(e) => {
            leftScrollTopRef.current = (e.currentTarget as HTMLDivElement).scrollTop;
          }}
        >
          <PdfConfig
            data={data}
            onChange={onChange}
            onRegisterFlush={handleRegisterPdfConfigFlush}
            isAutomated={isAutomated}
            lastPublishedAt={lastPublishedAt}
            automationFrequency={automationFrequency}
          />
        </Box>

        <Box
          ref={rightScrollRef}
          sx={styles.rightWrapper}
          onScroll={(e) => {
            rightScrollTopRef.current = (e.currentTarget as HTMLDivElement).scrollTop;
          }}
        >
          <Box
            sx={{
              ...styles.pdfWrapper,
              ...(templateFontFamily
                ? {
                    fontFamily: `'${templateFontFamily}', sans-serif`,
                  }
                : {}),
            }}
          >
            {(() => {
              const preview = (
                <FactsheetPreview
                  isPreviewStarted={isResetPreview}
                  data={data}
                  template={document?.template}
                  lineChartLoading={lineChartLoading}
                  title="RV Capital"
                  subtitle="Factsheet Preview"
                  onChartImageChange={handleChartImageChange}
                  onDataChange={onChange}
                  {...(([TEMPLATE_ID_691f80ce0664e043e4a1986d, TEMPLATE_ID_6a98f0c8b6c2a1d4e5f60001, TEMPLATE_ID_69d0c43f5a2b1c0d9e8f7a6b, TEMPLATE_ID_69842241581723c403c697fc, TEMPLATE_ID_6995ea9e4c4ced0db3932d1a, TEMPLATE_ID_6995ec804c4ced0db3932d1b, TEMPLATE_ID_6995ed834c4ced0db3932d1c, TEMPLATE_ID_6a56437d260e22f9e9296ede] as string[]).includes(templateId || '')
                    ? {
                        onChart1ImageChange: handleChart1ImageChange,
                        onChart2ImageChange: handleChart2ImageChange,
                        onChart3ImageChange: handleChart3ImageChange,
                        onPieChart1ImageChange: handlePieChart1ImageChange,
                        onPieChart2ImageChange: handlePieChart2ImageChange,
                        onPieChart3ImageChange: handlePieChart3ImageChange,
                      }
                    : {})}
                  onPieChartImagesChange={handlePieChartImagesChange}
                  onResetPreviewStarted={handleResetPreviewStarted}
                  onRegisterChartCapture={
                    ([
                      TEMPLATE_ID_68ef31e18875863b2fa59822,
                      TEMPLATE_ID_691f80ce0664e043e4a1986d,
                      TEMPLATE_ID_6a98f0c8b6c2a1d4e5f60001,
                      TEMPLATE_ID_69d0c43f5a2b1c0d9e8f7a6b,
                      TEMPLATE_ID_69842241581723c403c697fc,
                      TEMPLATE_ID_6995ea9e4c4ced0db3932d1a,
                      TEMPLATE_ID_6995ec804c4ced0db3932d1b,
                      TEMPLATE_ID_6995ed834c4ced0db3932d1c,
                      TEMPLATE_ID_6a56437d260e22f9e9296ede,
                    ] as string[]).includes(templateId || '')
                      ? handleRegisterChartCapture
                      : undefined
                  }
                />
              );
              return useFitPreviewWidth ? <FitPreviewWidth>{preview}</FitPreviewWidth> : preview;
            })()}
          </Box>
        </Box>
      </Box>

      <PdfPreviewDialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        data={data}
        template={document?.template}
        title={`${data?.title || 'Factsheet'} - Preview`}
        isin={document?.template?.isins?.[0] || data?.historyIsin || ''}
        until={resolveFactsheetUntilIso(data)}
      />
      
      {/* Automation Config Dialog */}
      {document && (
        <AutomationConfigDialog
          open={automationConfigOpen}
          template={document.template}
          isAutomated={isAutomated}
          automationFrequency={automationFrequency}
          onClose={handleAutomationConfigClose}
          onSave={handleAutomationConfigSave}
        />
      )}

      <ForceGenerateConfirmDialog
        open={forceGenerateOpen}
        todayLabel={todayLabel}
        nextScheduledLabel={nextScheduledLabel}
        isGenerating={testing}
        onClose={handleForceGenerateClose}
        onConfirm={handleForceGenerateConfirm}
      />
    </Box>
  );
};

export default PDFViewerV2;

